import './styles.scss';

import { Datetime, generateSearchableString } from '@/utils';
import { IncidentHistoryRow, useIncidentHistory } from '@/features/IncidentHistory';

import { IncidentHistoryFormDefinitions } from '@/features/IncidentHistory/components/IncidentHistoryForm/types';
import IncidentHistoryTableColumns from '@/features/IncidentHistory/components/IncidentHistoryTable/IncidentHistoryTableColumns';
import IncidentHistoryTableRow from '@/features/IncidentHistory/components/IncidentHistoryTable/IncidentHistoryTableRow';
import SimpleTable from '@/components/SimpleTable';
import { SortDirectionEnum } from '@/models/gen/graphql';

const IncidentHistoryTable = (): JSX.Element => {
  const [{ incidents: sortedIncidents }] = useIncidentHistory(({ incidents, sortBy, sortDirection, search = '' }) => ({
    incidents: Array.from(incidents.keys())
      .filter((id: string) => {
        if ((search ?? '') === '') return true;
        const accident = incidents.get(id);
        if (!accident) return false;
        const searchable = generateSearchableString(accident, IncidentHistoryFormDefinitions.searchable);
        return searchable.includes(search?.toLowerCase?.());
      })
      .sort((a: string, b: string): number => {
        if (!sortDirection || !sortBy) return 0;
        const sortIncidents = compareIncidentsBy(sortBy);
        const result = sortIncidents(incidents.get(a), incidents.get(b));
        if (sortDirection === SortDirectionEnum.Desc) return -result;
        return result;
      }),
  }));

  return (
    <SimpleTable
      className="IncidentHistoryTable"
      rows={sortedIncidents}
      columns={IncidentHistoryTableColumns}
      row={IncidentHistoryTableRow}
    />
  );
};

const compareIncidentsBy =
  (sortBy: string) =>
  (incidentA: IncidentHistoryRow, incidentB: IncidentHistoryRow): number => {
    if (!incidentA || !incidentB) return 0;
    switch (sortBy) {
      case 'STATION':
        return incidentA.station.localeCompare(incidentB.station);
      case 'INCIDENT_DATETIME':
        return new Datetime(incidentA.incidentDatetime).diff(incidentB.incidentDatetime, 'millisecond');
      case 'CREATED_AT':
        return new Datetime(incidentA.createdAt).diff(incidentB.createdAt, 'millisecond');
      case 'TRIP_SERVICER_IATA_AIRLINE_CODE':
        return incidentA.tripServicerIataAirlineCode.localeCompare(incidentB.tripServicerIataAirlineCode);
      case 'TRIP_FLIGHT_NAME':
        let flightNameA = incidentA.tripLoopName || incidentA.tripFlightNumber.toString();
        let flightNameB = incidentB.tripLoopName || incidentB.tripFlightNumber.toString();
        return flightNameA.localeCompare(flightNameB);
      case 'TRIP_SCHEDULED':
        return new Datetime(incidentA.tripScheduled).diff(incidentB.tripScheduled, 'millisecond');
      case 'VEHICLE_TRACKING_ID':
        return incidentA.vehicleTrackingId.localeCompare(incidentB.vehicleTrackingId);
      case 'EMPLOYEE_NAME':
        return [incidentA.driverFirstName, incidentA.driverLastName]
          .join(' ')
          .localeCompare([incidentB.driverFirstName, incidentB.driverLastName].join(' '));
      case 'DRIVER_EMPLOYEE_ID':
        return incidentA.driverEmployeeId.localeCompare(incidentB.driverEmployeeId);
      case 'MANAGER_NAME':
        return [incidentA.managerFirstName, incidentA.managerLastName]
          .join(' ')
          .localeCompare([incidentB.managerFirstName, incidentB.managerLastName].join(' '));
      case 'CREW_INVOLVED':
        return incidentA.crewInvolved === incidentB.crewInvolved ? 0 : incidentA.crewInvolved ? -1 : 1;
      default:
        return 0;
    }
  };

export default IncidentHistoryTable;
